import { apiClient } from '../lib/apiClient';

/**
 * weekly-report-search 用の共通パラメータ組み立て・取得ユーティリティ
 *  - 各画面の検索ロジックを共通化
 */

export type SimpleOption = {
  id: string;
  value: string;
  user_id?: string | null;
  legacy?: boolean;
};
export type ReportTypeLike = { id: number | string; label: string };

export type WeeklyReportCreatorOption = SimpleOption & {
  department_id?: string;
  user_id?: string | null;
  legacy?: boolean;
};

export interface WeeklyReportCreatorFetchArgs {
  startDate: string;
  endDate: string;
  selectedDepartmentIds?: Array<string | number>;
  selectedTeamIds?: Array<string | number>;
  /** Overwrite Old では下書き(temporary_flg=1)の発行者も候補に含める。 */
  includeTemporary?: boolean;
  userDep?: string;
  userTeamId?: string | number;
  signal?: AbortSignal;
}

export interface WeeklyReportCreatorSnapshot {
  options: WeeklyReportCreatorOption[];
  cursor: number | null;
}

export interface WeeklyReportCreatorDelta {
  nextCursor: number;
  resetRequired: boolean;
  upserts: WeeklyReportCreatorOption[];
  deletedIds: string[];
}

export interface WeeklyReportCreatorPage {
  items: WeeklyReportCreatorOption[];
  nextPageCursor: string | null;
  hasMore: boolean;
  syncCursor: number | null;
}

export interface WeeklySearchBuildArgs {
  // wr_id が指定された場合は、それ以外の条件は無視する
  wrId?: string | number;
  // 画面側で YYYY-MM-DD に整形済みの文字列を渡す（タイムゾーン差異を避けるため）
  startDate?: string;
  endDate?: string;
  freeWord?: string;
  searchTitleBody?: boolean;
  searchTags?: boolean;

  // 週報分類: 画面の選択は id 配列、master から label を解決して送る
  selectedReportTypeIds?: string[];
  allReportTypes?: ReportTypeLike[];

  // チーム / 課: API には id を送る
  selectedTeamIds?: string[];
  selectedDepartmentIds?: Array<string | number>;

  // 記入者: id or label が入ってくる前提。options から label を解決（あれば）
  selectedCreateByValues?: string[];
  // URL復元など、候補取得前でも user_id と確定している値
  selectedCreateByUserIds?: string[];
  createByOptions?: SimpleOption[];

  // WeeklyReport/SearchArea など一部画面では 0 を必ず付与する
  appendTemporaryFlg0?: boolean;
}

/** 内部ユーティリティ: ラベル解決（id または value に一致すれば value を返す） */
function resolveCreateByLabel(options: SimpleOption[] | undefined, idOrName: string): string {
  if (!options || options.length === 0) return idOrName;
  const found = options.find(o => String(o.id) === String(idOrName) || String(o.value) === String(idOrName));
  return found ? found.value : idOrName;
}

/** 内部ユーティリティ: report_types のラベル解決（id -> label） */
function resolveReportTypeLabel(all: ReportTypeLike[] | undefined, id: string): string {
  if (!all || all.length === 0) return id;
  const found = all.find(r => String(r.id) === String(id));
  return found?.label ?? id;
}

/**
 * 検索パラメータを URLSearchParams に組み立てる
 */
export function buildWeeklySearchParams(args: WeeklySearchBuildArgs): URLSearchParams {
  const {
    wrId,
    startDate,
    endDate,
    freeWord,
    searchTitleBody,
    searchTags,
    selectedReportTypeIds = [],
    allReportTypes = [],
    selectedTeamIds = [],
    selectedDepartmentIds = [],
    selectedCreateByValues = [],
    selectedCreateByUserIds = [],
    createByOptions = [],
    appendTemporaryFlg0 = false,
  } = args;

  const params = new URLSearchParams();

  if (wrId != null && String(wrId).trim() !== '') {
    params.set('wr_id', String(wrId).trim());
    return params;
  }

  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const freeWordTrimmed = typeof freeWord === 'string' ? freeWord.trim() : '';
  const hasFreeWord = freeWordTrimmed.length > 0;
  const hasSearchFlags =
    typeof searchTitleBody === 'boolean' || typeof searchTags === 'boolean';
  const useTitleBody =
    typeof searchTitleBody === 'boolean' ? searchTitleBody : true;
  const useTags = typeof searchTags === 'boolean' ? searchTags : false;

  if (hasFreeWord && (useTitleBody || useTags)) {
    params.append('free_word', freeWordTrimmed);
  }
  // フリーワードが空でも、明示フラグは送る（検索対象の保持用）
  if (hasSearchFlags) {
    params.append('search_title_body', useTitleBody ? '1' : '0');
    params.append('search_tags', useTags ? '1' : '0');
  }

  // report_types は label を送る
  selectedReportTypeIds.forEach(id => {
    params.append('report_types', resolveReportTypeLabel(allReportTypes, id));
  });

  // teams / departments は id を送る
  selectedTeamIds.forEach(id => params.append('teams', String(id)));
  selectedDepartmentIds.forEach(id => params.append('departments', String(id)));

  // 新しい候補は create_by_user_id、旧/legacy候補は従来どおり氏名を送る。
  // 両方が指定された場合、バックエンドは OR 条件として扱う。
  const explicitUserIds = new Set(
    selectedCreateByUserIds.map((value) => String(value ?? '').trim()).filter(Boolean)
  );
  explicitUserIds.forEach((userId) => params.append('create_by_user_ids', userId));

  selectedCreateByValues.forEach(idOrName => {
    if (explicitUserIds.has(String(idOrName).trim())) return;
    const found = createByOptions.find(
      o => String(o.id) === String(idOrName)
    );
    const userId = String(found?.user_id ?? '').trim();
    if (found && !found.legacy && userId) {
      if (!explicitUserIds.has(userId)) {
        params.append('create_by_user_ids', userId);
      }
      return;
    }
    if (found?.legacy) {
      params.append('legacy_create_by', found.value);
      return;
    }
    // 表示名で渡された旧URL/旧キャッシュ値は user_id に推測変換しない。
    // 同姓同名では候補を一意に決められないため、従来の氏名条件を維持する。
    params.append('create_by', resolveCreateByLabel(createByOptions, idOrName));
  });

  if (appendTemporaryFlg0) {
    params.set('temporary_flg', '0');
  }

  return params;
}

/**
 * weekly-report-search を実行して結果配列を返す
 */
export async function fetchWeeklyReports<T = any>(params: URLSearchParams, signal?: AbortSignal): Promise<T[]> {
  const resp = await apiClient.get<T[]>('/weekly-report-search', { params, signal });
  return Array.isArray(resp.data) ? resp.data : [];
}

/**
 * 期間・課・公開範囲に該当する発行者候補を取得する。
 *
 * 週報本体の検索結果から候補を逆算すると検索APIの件数上限に影響されるため、
 * 発行者専用APIを利用する。レスポンスの型揺れはここで吸収し、画面には常に
 * `id` / `value` を持つ候補だけを返す。
 */
function buildCreatorParams(args: WeeklyReportCreatorFetchArgs): URLSearchParams {
  const params = new URLSearchParams();
  params.set('start_date', args.startDate);
  params.set('end_date', args.endDate);

  (args.selectedDepartmentIds ?? []).forEach((id) => {
    const value = String(id ?? '').trim();
    if (value) params.append('departments', value);
  });

  (args.selectedTeamIds ?? []).forEach((id) => {
    const value = String(id ?? '').trim();
    if (value) params.append('teams', value);
  });

  if (args.includeTemporary) params.set('include_temporary', '1');

  const userDep = String(args.userDep ?? '').trim();
  if (userDep) params.set('USER_DEP', userDep);
  const userTeamId = String(args.userTeamId ?? '').trim();
  if (userTeamId) params.set('USER_TEAM_ID', userTeamId);
  return params;
}

function normalizeCreatorOptions(rows: unknown): WeeklyReportCreatorOption[] {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row: any): WeeklyReportCreatorOption | null => {
      const id = String(
        row?.id ?? row?.user_id ?? row?.create_by_user_id ?? ''
      ).trim();
      const value = String(
        row?.value ?? row?.label ?? row?.create_by ?? ''
      ).trim();
      if (!id || !value) return null;

      const departmentIdRaw =
        row?.department_id ?? row?.departmentId ?? row?.department;
      const departmentId =
        departmentIdRaw == null || String(departmentIdRaw).trim() === ''
          ? undefined
          : String(departmentIdRaw).trim();
      const userIdRaw = row?.user_id ?? row?.create_by_user_id;
      const userId =
        userIdRaw == null || String(userIdRaw).trim() === ''
          ? null
          : String(userIdRaw).trim();

      return {
        id,
        value,
        department_id: departmentId,
        user_id: userId,
        legacy: Boolean(row?.legacy),
      };
    })
    .filter((item): item is WeeklyReportCreatorOption => item != null);
}

export async function fetchWeeklyReportCreatorSnapshot(
  args: WeeklyReportCreatorFetchArgs,
  maxResults?: number
): Promise<WeeklyReportCreatorSnapshot> {
  const params = buildCreatorParams(args);
  if (maxResults != null && Number.isFinite(maxResults)) {
    params.set('max_results', String(Math.max(1, Math.floor(maxResults))));
  }
  const resp = await apiClient.get<any[]>('/weekly-report-creators', {
    params,
    signal: args.signal,
  });
  const cursorRaw = resp.headers?.['x-creator-sync-cursor'];
  const parsedCursor = Number(cursorRaw);
  return {
    options: normalizeCreatorOptions(resp.data),
    cursor: Number.isSafeInteger(parsedCursor) && parsedCursor >= 0 ? parsedCursor : null,
  };
}

export async function fetchWeeklyReportCreatorDelta(
  args: WeeklyReportCreatorFetchArgs,
  cursor: number
): Promise<WeeklyReportCreatorDelta> {
  const params = buildCreatorParams(args);
  params.set('cursor', String(cursor));
  const resp = await apiClient.get<any>('/weekly-report-creators-sync', {
    params,
    signal: args.signal,
  });
  const data = resp.data ?? {};
  const nextCursor = Number(data.next_cursor);
  return {
    nextCursor:
      Number.isSafeInteger(nextCursor) && nextCursor >= 0 ? nextCursor : cursor,
    resetRequired: Boolean(data.reset_required),
    upserts: normalizeCreatorOptions(data.upserts),
    deletedIds: Array.isArray(data.deleted_ids)
      ? data.deleted_ids.map(String).filter((id: string) => id.trim().length > 0)
      : [],
  };
}

export async function fetchWeeklyReportCreatorPage(
  args: WeeklyReportCreatorFetchArgs,
  options: {
    pageSize: number;
    pageCursor?: string | null;
    query?: string;
  }
): Promise<WeeklyReportCreatorPage> {
  const params = buildCreatorParams(args);
  const query = String(options.query ?? '').trim();
  const pageSizeLimit = query ? 200 : 1000;
  params.set(
    'page_size',
    String(Math.max(1, Math.min(pageSizeLimit, Math.floor(options.pageSize))))
  );
  if (options.pageCursor) params.set('page_cursor', options.pageCursor);
  if (query) params.set('q', query);

  const resp = await apiClient.get<any>('/weekly-report-creators-page', {
    params,
    signal: args.signal,
  });
  const data = resp.data ?? {};
  const syncCursor = Number(data.sync_cursor);
  return {
    items: normalizeCreatorOptions(data.items),
    nextPageCursor:
      typeof data.next_page_cursor === 'string' && data.next_page_cursor
        ? data.next_page_cursor
        : null,
    hasMore: Boolean(data.has_more),
    syncCursor:
      Number.isSafeInteger(syncCursor) && syncCursor >= 0 ? syncCursor : null,
  };
}

export async function fetchWeeklyReportCreators(
  args: WeeklyReportCreatorFetchArgs
): Promise<WeeklyReportCreatorOption[]> {
  return (await fetchWeeklyReportCreatorSnapshot(args)).options;
}

type DepartmentScope = Array<string | number>;
type DateRange = { start: string; end: string };

export interface WeeklyAdaptiveFetchArgs {
  startDate: string;
  endDate: string;
  baseParams?: URLSearchParams;
  departmentScopes: DepartmentScope[];
  userDep?: string;
  userTeamId?: string | number;
  appendTemporaryFlg0?: boolean;
  splitThreshold?: number;
  initialChunkDays?: number;
  minChunkDays?: number;
  maxConcurrency?: number;
  outerMaxConcurrency?: number;
  signal?: AbortSignal;
}

export interface WeeklyAdaptiveFetchResult<T = any> {
  rows: T[];
  exact: boolean;
}

const parseYmdAsUtc = (ymd: string): Date => {
  const [y, m, d] = String(ymd).split('-').map((v) => Number(v));
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};

const toYmdFromUtc = (dt: Date): string => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDaysUtc = (dt: Date, days: number): Date => {
  const next = new Date(dt);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const normalizeRange = (startDate: string, endDate: string): DateRange => {
  const s = parseYmdAsUtc(startDate);
  const e = parseYmdAsUtc(endDate);
  const [min, max] = s <= e ? [s, e] : [e, s];
  return { start: toYmdFromUtc(min), end: toYmdFromUtc(max) };
};

const rangeDaysInclusive = (range: DateRange): number => {
  const s = parseYmdAsUtc(range.start).getTime();
  const e = parseYmdAsUtc(range.end).getTime();
  const diff = Math.floor((e - s) / 86400000);
  return Math.max(diff + 1, 1);
};

const splitDateRangeInHalf = (range: DateRange): DateRange[] => {
  const totalDays = rangeDaysInclusive(range);
  if (totalDays <= 1) return [range];

  const start = parseYmdAsUtc(range.start);
  const leftDays = Math.floor(totalDays / 2);
  const leftEnd = addDaysUtc(start, Math.max(leftDays - 1, 0));
  const rightStart = addDaysUtc(leftEnd, 1);

  return [
    { start: range.start, end: toYmdFromUtc(leftEnd) },
    { start: toYmdFromUtc(rightStart), end: range.end },
  ];
};

const buildDateChunks = (
  startDate: string,
  endDate: string,
  chunkDays: number
): DateRange[] => {
  const normalized = normalizeRange(startDate, endDate);
  const from = parseYmdAsUtc(normalized.start);
  const to = parseYmdAsUtc(normalized.end);
  const size = Math.max(1, Math.trunc(chunkDays));
  const chunks: DateRange[] = [];

  let cursor = from;
  while (cursor <= to) {
    const chunkEndCandidate = addDaysUtc(cursor, size - 1);
    const chunkEnd = chunkEndCandidate <= to ? chunkEndCandidate : to;
    chunks.push({
      start: toYmdFromUtc(cursor),
      end: toYmdFromUtc(chunkEnd),
    });
    cursor = addDaysUtc(chunkEnd, 1);
  }

  return chunks;
};

const normalizeDepartmentScope = (scope: DepartmentScope | undefined): string[] =>
  Array.from(
    new Set(
      (scope ?? [])
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0)
    )
  ).sort();

const buildAdaptiveSearchParams = (
  args: WeeklyAdaptiveFetchArgs,
  range: DateRange,
  scope: string[]
): URLSearchParams => {
  if (!args.baseParams) {
    const params = buildWeeklySearchParams({
      startDate: range.start,
      endDate: range.end,
      selectedDepartmentIds: scope,
      appendTemporaryFlg0: Boolean(args.appendTemporaryFlg0),
    });
    if (args.userDep) {
      params.set('USER_DEP', args.userDep);
    }
    if (args.userTeamId) {
      params.set('USER_TEAM_ID', String(args.userTeamId));
    }
    return params;
  }

  const params = new URLSearchParams(args.baseParams);
  params.delete('start_date');
  params.delete('end_date');
  params.delete('departments');
  params.delete('USER_DEP');
  params.delete('USER_TEAM_ID');

  params.set('start_date', range.start);
  params.set('end_date', range.end);
  scope.forEach((id) => params.append('departments', id));

  if (args.appendTemporaryFlg0) {
    params.set('temporary_flg', '0');
  }
  if (args.userDep) {
    params.set('USER_DEP', args.userDep);
  }
  if (args.userTeamId) {
    params.set('USER_TEAM_ID', String(args.userTeamId));
  }

  return params;
};

const isAbortLikeError = (err: unknown): boolean => {
  const e = err as any;
  const code = String(e?.code ?? '');
  const name = String(e?.name ?? '');
  const message = String(e?.message ?? '');
  return (
    code === 'ERR_CANCELED' ||
    name === 'AbortError' ||
    message.toLowerCase().includes('aborted') ||
    message.toLowerCase().includes('canceled')
  );
};

const runWithConcurrency = async <T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number
): Promise<Array<PromiseSettledResult<T>>> => {
  if (tasks.length === 0) return [];

  const limit = Math.max(1, Math.trunc(concurrency));
  const settled: Array<PromiseSettledResult<T>> = new Array(tasks.length);
  let cursor = 0;

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (true) {
      const idx = cursor++;
      if (idx >= tasks.length) return;
      try {
        const value = await tasks[idx]();
        settled[idx] = { status: 'fulfilled', value };
      } catch (reason) {
        settled[idx] = { status: 'rejected', reason };
      }
    }
  });

  await Promise.all(workers);
  return settled;
};

const dedupeRowsByWrId = <T>(rows: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];

  rows.forEach((row) => {
    const wrId =
      row && typeof row === 'object' ? (row as any).wr_id : undefined;
    if (wrId == null || String(wrId).trim() === '') {
      out.push(row);
      return;
    }
    const key = String(wrId);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });

  return out;
};

const fetchWeeklyReportsBySingleScopeAdaptively = async <T = any>(
  args: WeeklyAdaptiveFetchArgs,
  scope: string[]
): Promise<WeeklyAdaptiveFetchResult<T>> => {
  const splitAt = Math.max(0, Math.trunc(args.splitThreshold ?? 100));
  const minChunkDays = Math.max(1, Math.trunc(args.minChunkDays ?? 1));
  const initialChunkDays = Math.max(1, Math.trunc(args.initialChunkDays ?? 31));
  const maxConcurrency = Math.max(1, Math.trunc(args.maxConcurrency ?? 4));

  let exact = true;
  const rows: T[] = [];
  let splitRanges = buildDateChunks(args.startDate, args.endDate, initialChunkDays);

  const shouldSplitRange = (range: DateRange, count: number): boolean => {
    if (splitAt <= 0) return false;
    if (count < splitAt) return false;
    return rangeDaysInclusive(range) > minChunkDays;
  };

  const isSaturatedRange = (count: number): boolean => {
    if (splitAt <= 0) return false;
    return count >= splitAt;
  };

  while (splitRanges.length > 0) {
    const currentRanges = splitRanges;
    splitRanges = [];

    const settled = await runWithConcurrency(
      currentRanges.map((range) => async () => {
        const params = buildAdaptiveSearchParams(args, range, scope);

        const fetchedRows = await fetchWeeklyReports<T>(params, args.signal);
        if (shouldSplitRange(range, fetchedRows.length)) {
          return {
            rows: [] as T[],
            nextRanges: splitDateRangeInHalf(range),
            saturated: false,
          };
        }

        return {
          rows: fetchedRows,
          nextRanges: [] as DateRange[],
          saturated: isSaturatedRange(fetchedRows.length),
        };
      }),
      maxConcurrency
    );

    for (const item of settled) {
      if (item.status === 'rejected') {
        if (isAbortLikeError(item.reason)) {
          throw item.reason;
        }
        exact = false;
        continue;
      }

      rows.push(...item.value.rows);
      if (item.value.nextRanges.length > 0) {
        splitRanges.push(...item.value.nextRanges);
      }
      if (item.value.saturated) {
        exact = false;
      }
    }
  }

  return { rows: dedupeRowsByWrId(rows), exact };
};

export async function fetchWeeklyReportsByDepartmentScopesAdaptively<T = any>(
  args: WeeklyAdaptiveFetchArgs
): Promise<WeeklyAdaptiveFetchResult<T>> {
  const normalized = normalizeRange(args.startDate, args.endDate);
  const scopesRaw = args.departmentScopes && args.departmentScopes.length > 0
    ? args.departmentScopes
    : [[]];
  const scopes = scopesRaw.map((s) => normalizeDepartmentScope(s));
  const outerMaxConcurrency = Math.max(1, Math.trunc(args.outerMaxConcurrency ?? 1));

  const settled = await runWithConcurrency(
    scopes.map((scope) => () =>
      fetchWeeklyReportsBySingleScopeAdaptively<T>(
        {
          ...args,
          startDate: normalized.start,
          endDate: normalized.end,
        },
        scope
      )
    ),
    outerMaxConcurrency
  );

  let exact = true;
  const rows: T[] = [];

  settled.forEach((item) => {
    if (item.status === 'rejected') {
      if (isAbortLikeError(item.reason)) {
        throw item.reason;
      }
      exact = false;
      return;
    }

    rows.push(...item.value.rows);
    if (!item.value.exact) {
      exact = false;
    }
  });

  return {
    rows: dedupeRowsByWrId(rows),
    exact,
  };
}
